import model from "../model";
import type { SoarPayClientConfigDefinition } from "../types/client-config-types";
import type { SoarPayMethodsResponse, SoarPayPaymentType, SoarPayPaymentStatusResponse, SoarPaySendParams, SoarPayMethod, SoarPayPreparePaymentResponse, SoarPayCancelTransactionResponse } from "../types/soar-pay-types";
import cookieHelper from "../utils/cookie-helper";
import { addQueryParams, deepMerge, getOS } from "../utils/utils";
import { addCloudflareHeaders } from "./cloudflare-management";
// import { getStorageProbeParams } from "../utils/storage-probe";
import sendServerRequest from "./send-server-request";
import { testSoarPayIconAcc, testSoarPayMethods } from "./soar-pay-service-test-cases";

type ProcessPaymentOptions = {
    provider: string, 
    amount: string, 
    paymentType: SoarPayPaymentType, 
    service: string,
    requestBody: object, 
    global: boolean,
};

type PreparePaymentOptions = {
    provider: string, 
    method: SoarPayPaymentType, 
    parameters: {
        amount?: string,
        cryptoCurrency?: string,
        sessionId?: string,
        userId?: string,
        merchantId?: string,
        service?: string,
        successUrl?: string,
        attributes?: {
            pscMethod?: string,
            successUrl?: string,
            failureUrl?: string,
            cancelUrl?: string,
            channelId?: string,
        },
    },
};

let soarPayConfig: SoarPayClientConfigDefinition | undefined = undefined;

export default {
    async cancelTransaction(transactionId: string | number, global: boolean): Promise<SoarPayCancelTransactionResponse | undefined>{
        if(!soarPayConfig) return;

        let url = `${soarPayConfig.baseUrl}/user/transaction/${soarPayConfig.merchant}/${model.userProfile?.userName}/${transactionId}`;

        return await send({
            url, 
            global,
            method: 'DELETE',
        });
    },
    async preparePayment({provider, method, parameters}: PreparePaymentOptions): Promise<SoarPayPreparePaymentResponse | undefined>{
        if(!soarPayConfig) return;

        let url = `${soarPayConfig.baseUrl}/${provider}/payment/prepare/${soarPayConfig.merchant}/${model.userProfile?.userName}`;
        url = addQueryParams(url, {
            method,
        });

        return await send({url, method: 'POST', data: JSON.stringify({parameters}), global: true});
    },
    async getTransactionStatus(transactionId: string | number, global: boolean): Promise<SoarPayPaymentStatusResponse | undefined>{
        if(!soarPayConfig) return;
        let url = `${soarPayConfig.baseUrl}/user/transaction/${soarPayConfig.merchant}/${model.userProfile?.userName}/${transactionId}/status`;
        return await send({url, global});
    },
    async getMethods(paymentType?: SoarPayPaymentType, global?: boolean){
        if(!soarPayConfig) return;

        let paymentTypes: SoarPayPaymentType[] = paymentType ? [paymentType] : ['deposit', 'withdrawal'];
        let activeRequests: Promise<SoarPayMethodsResponse>[] = [];

        if (import.meta.env.DEV){
            if(testSoarPayMethods(paymentTypes)) return;
        }

        for(let pType of paymentTypes){
            let url = `${soarPayConfig.baseUrl}/user/payment/method/${soarPayConfig.merchant}/${model.userProfile?.userName}`;
            url = addQueryParams(url, {
                method: pType,
                channelId: model.channelId + ':' + getOS(),
            });
            activeRequests.push(send({url, global}));
        }

        model.soarPay.methods ||= {};

        const resps = await Promise.all(activeRequests);
        if(!model.userLoggedIn) return;

        for(let i = 0, len = paymentTypes.length; i < len; i++){
            let pType = paymentTypes[i];
            if(!pType) continue;
            
            const methods = model.soarPay.methods;
            if(methods) methods[pType] = resps[i];

            if(import.meta.env.DEV) testSoarPayIconAcc(resps[i]);
        }
    },
    async processPayment({provider, amount, paymentType, service, requestBody, global}: ProcessPaymentOptions): Promise<SoarPayPaymentStatusResponse | undefined>{
        if(!soarPayConfig) return;

        let successUrl = soarPayConfig.paymentStatusPages?.[paymentType === 'deposit' ? 'depositSuccess' : 'withdrawSuccess'];
        let failureUrl = soarPayConfig.paymentStatusPages?.[paymentType === 'deposit' ? 'depositFailure' : 'withdrawFailure'];

        return await send({
            url: `${soarPayConfig.baseUrl}/${provider}/${paymentType}/process/`, 
            method: 'POST',
            data: JSON.stringify(deepMerge({}, {
                sessionId: model.uToken,
                userId: model.userProfile?.userName || cookieHelper.get('username') || undefined,
                merchantId: soarPayConfig.merchant,
                amount,
                service,
                attributes: {
                    successUrl: successUrl,
                    failureUrl: failureUrl,
                    cancelUrl: failureUrl,
                    channelId: model.channelId,
                },
            }, requestBody)),
            global,
        });
    },
    setConfig(config?: SoarPayClientConfigDefinition){
        soarPayConfig = config;
    },
    getConfig(){
        return soarPayConfig;
    },
};

async function send({url, method = 'GET', data, global = false}: SoarPaySendParams){
    url = addQueryParams(url, {
        sessionId: model.uToken,
        locale: model.locale.replace('-', '_'),
        lobbyVersion: model.lobbyVersion,
    });

    //STORAGE TEST START
    // url = addQueryParams(url, getStorageProbeParams());
    //STORAGE TEST END

    const headers: HeadersInit = {
        'Content-Type': 'application/json; charset=utf-8',
    };

    addCloudflareHeaders(headers);

    let resp = await sendServerRequest(url, {
        method,
        headers,
        body: data,
    }, { global, ignoreDocVisibility: true, retryOnlyOnGet: true, trackAbort: true });

    if(!resp) return;

    return await resp.json();
}

export async function deleteAccount(accountId: string){
    const soarPayConfig = model.clientConfig.apiEndpoints?.soarPay;
    if(!soarPayConfig) return;

    return await send({
        url: `${soarPayConfig.baseUrl}/user/account/${soarPayConfig.merchant}/${model.userProfile?.userName}/${accountId}`, 
        method: 'DELETE',
        global: true,
    });
}